import { Box, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import ExpandableText from '../common/ExpandableText';
import LessonScoreCell from './LessonScoreCell';
import HomeworkCell from './HomeworkCell';

const B = { primary: '#4357AD', dark: '#1E2A4A' };

/**
 * One lesson as a full-width card — the narrow-screen stand-in for a table row.
 * Prose gets the whole card width instead of a ~60px column, which is what
 * removes the word-fragment wrapping; the note itself stays clamped so a long
 * entry does not push the rest of the list off screen.
 *
 * Page-specific bits (payment toggle, audit trail, action buttons) come in as
 * slots so tutor and admin journals keep their own behaviour.
 */
const LessonCardMobile = ({ lesson, rowNum, statusSlot, metaSlot, actionsSlot }) => {
  const deleted = Boolean(lesson.deleted);

  return (
    <Box
      sx={{
        px: 2,
        py: 1.75,
        borderBottom: '1px solid #F1F0FB',
        bgcolor: deleted ? '#FEF2F2' : '#fff',
        opacity: deleted ? 0.7 : 1,
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      {/* Row 1 — number, date, actions */}
      <Stack direction="row" alignItems="flex-start" spacing={1.25}>
        <Box
          sx={{
            flexShrink: 0,
            width: 30,
            height: 30,
            borderRadius: 2,
            bgcolor: deleted ? '#EF4444' : B.primary,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: 12,
          }}
        >
          {deleted ? '—' : String(rowNum).padStart(2, '0')}
        </Box>

        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={700} sx={{ color: B.dark, lineHeight: 1.3 }}>
            {dayjs(lesson.date).format('DD.MM.YYYY')}
          </Typography>
          <Typography variant="caption" sx={{ color: '#8891A5' }}>
            {dayjs(lesson.date).format('dddd')}
            {lesson.scheduleStartTime && <>, {dayjs(lesson.scheduleStartTime).format('HH:mm')}</>}
          </Typography>
        </Box>

        {actionsSlot && <Box sx={{ flexShrink: 0 }}>{actionsSlot}</Box>}
      </Stack>

      {/* Row 2 — topic */}
      <Typography
        lang="uk"
        variant="body2"
        fontWeight={700}
        sx={{
          mt: 1,
          color: lesson.topic ? B.dark : '#8891A5',
          fontStyle: lesson.topic ? 'normal' : 'italic',
          hyphens: 'auto',
          overflowWrap: 'break-word',
          wordBreak: 'normal',
        }}
      >
        {lesson.topic || 'Не заповнено'}
      </Typography>

      {/* Row 3 — notes */}
      {lesson.notes ? (
        <ExpandableText
          lines={2}
          sx={{ mt: 0.75, bgcolor: '#F8F7FF', border: '1px solid #EDE9FE', borderRadius: 2, px: 1.25, py: 0.75 }}
        >
          {lesson.notes}
        </ExpandableText>
      ) : (
        <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: '#C4B5FD', fontStyle: 'italic' }}>
          Без приміток
        </Typography>
      )}

      {/* Row 3.5 — homework (shown only when set, to keep cards compact) */}
      {(lesson.homeworkTask || (lesson.homeworkFiles || []).length > 0) && (
        <Box sx={{ mt: 0.75 }}>
          <Typography variant="caption" fontWeight={600} sx={{ color: '#8891A5', display: 'block', mb: 0.25 }}>
            Домашнє завдання
          </Typography>
          <HomeworkCell lesson={lesson} />
        </Box>
      )}

      {metaSlot && <Box sx={{ mt: 0.75 }}>{metaSlot}</Box>}

      {/* Row 4 — score + status */}
      <Box sx={{ mt: 1.25, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75 }}>
        <LessonScoreCell lesson={lesson} />
        {statusSlot}
      </Box>
    </Box>
  );
};

export default LessonCardMobile;
